import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

export const PASSWORD_MIN_LENGTH = 8;

/**
 * Single source of truth for new-password complexity rules. The list is
 * library-agnostic (plain predicates + label getters) so it can drive both the
 * Zod schema (submit gating, via {@link getNewPasswordSchema}) and the live
 * `PasswordRequirements` checklist (visual feedback) without drifting apart.
 * When yup validation is removed, this could be migrated directly to a zod schema
 */
export type PasswordRequirement = {
  id: string;
  isSatisfiedBy: (password: string) => boolean;
  getLabel: (t: TFunction) => string;
};

export const passwordRequirements: ReadonlyArray<PasswordRequirement> = [
  {
    id: "minLength",
    isSatisfiedBy: (password) => password.length >= PASSWORD_MIN_LENGTH,
    getLabel: (t) =>
      t(Resources.Validation.Password.MinLength, {
        length: PASSWORD_MIN_LENGTH,
      }),
  },
  {
    id: "lowercase",
    isSatisfiedBy: (password) => /[a-z]/.test(password),
    getLabel: (t) => t(Resources.Validation.Password.MinLowercase),
  },
  {
    id: "uppercase",
    isSatisfiedBy: (password) => /[A-Z]/.test(password),
    getLabel: (t) => t(Resources.Validation.Password.MinUppercase),
  },
  {
    id: "number",
    isSatisfiedBy: (password) => /[0-9]/.test(password),
    getLabel: (t) => t(Resources.Validation.Password.MinNumber),
  },
];

/**
 * Reusable Zod schema for a new-password field. Enforces "required" plus every
 * rule in {@link passwordRequirements}. Infers as `string`.
 */
export const getNewPasswordSchema = (t: TFunction) =>
  z
    .string()
    .min(1, t(Resources.Validation.Required))
    .superRefine((password, ctx) => {
      for (const requirement of passwordRequirements) {
        if (!requirement.isSatisfiedBy(password)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requirement.getLabel(t),
          });
        }
      }
    });
